/**
 * Terms and privacy pages, as markdown.
 *
 * These are long prose documents with no layout to speak of, so they stay as
 * markdown rather than becoming Svelte components — easier to diff and to hand
 * to a lawyer.
 *
 * mdsvex compiles them to Svelte components, so the body cannot cross a server
 * `load` boundary and is resolved in a universal one.
 */

export interface LegalMeta {
	title: string;
	path: string;
	seoTitle?: string;
	seoDescription?: string;
}

type LegalModule = { metadata: LegalMeta; default: unknown };

/**
 * Front matter only — note the `import` option, which is load-bearing.
 *
 * An eager glob without it imports each module *whole*, and mdsvex compiles
 * these documents to Svelte components — the terms in every locale plus the
 * privacy policy in English and Thai, ten files today — as compiled render
 * functions. `Footer.svelte` calls `legalPath` to build two links, and the footer
 * is in the root layout, so that put the whole corpus into the client bundle of
 * every page on the site to produce two `href`s. Measured at nine files it was
 * 145 KB of legal prose, 35 KB over the wire; it only grows as locales are added,
 * which is the point.
 *
 * Naming the export means only `metadata` is pulled in and the component tree
 * shakes away. `loaders` stays lazy and unchanged — that is what actually
 * renders a legal page, on the one route that shows one.
 */
const modules = import.meta.glob<LegalMeta>('../content/legal/*.md', {
	eager: true,
	import: 'metadata'
});
const loaders = import.meta.glob<LegalModule>('../content/legal/*.md');

const nameOf = (path: string) => path.split('/').pop()!.replace(/\.md$/, '');

/**
 * Path of a legal page in one locale, e.g. `legalPath('terms', 'th')`.
 *
 * Legal pages are *not* in the `$lib/pages` registry — they are markdown with
 * their localised path in front matter — so `pagePath('terms', locale)` returns
 * `undefined` for them. Anything linking to the terms or privacy page through
 * that helper silently rendered a dead link, which is what happened to the
 * footer's legal bar and to the checkout's terms checkbox.
 *
 * Falls back to English: only `terms` is translated into all eight locales,
 * and an English policy is more use to a reader than no link at all.
 */
export function legalPath(kind: 'terms' | 'privacy', locale: string): string | undefined {
	const pages = getLegalPages();
	return (
		pages.find((page) => page.name === `${kind}.${locale}`)?.path ??
		pages.find((page) => page.name === `${kind}.en`)?.path
	);
}

/** All legal pages, for route enumeration. */
export function getLegalPages(): (LegalMeta & { name: string })[] {
	return Object.entries(modules).map(([path, metadata]) => ({
		...metadata,
		name: nameOf(path)
	}));
}

export function getLegalPage(path: string): (LegalMeta & { name: string }) | undefined {
	const wanted = path.replace(/\/$/, '');
	return getLegalPages().find((page) => page.path === wanted);
}

export async function loadLegalBody(name: string): Promise<unknown> {
	const resolver = loaders[`../content/legal/${name}.md`];
	if (!resolver) return undefined;
	return (await resolver()).default;
}
